"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.executeRefundResponseSchema = exports.executeRefundRequestSchema = exports.refundPreviewResponseSchema = exports.refundQuoteSummarySchema = exports.refundQuoteLineSchema = exports.refundPreviewIssueSchema = exports.refundPreviewIssueCodeSchema = exports.refundPaymentIntentParamsSchema = exports.refundPreviewRequestSchema = exports.customRefundPreviewRequestSchema = exports.itemizedRefundPreviewRequestSchema = exports.refundItemSelectionSchema = exports.aodSignatureHeadersSchema = exports.refundReasonSchema = exports.refundReturnStatusSchema = exports.buildAodSigningMessage = exports.canonicalizeAodPayload = exports.AOD_SIGNATURE_VERSION = void 0;
const zod_1 = require("zod");
const base_js_1 = require("../common/base.js");
exports.AOD_SIGNATURE_VERSION = "nectarpay-aod-v1";
const canonicalizeAodPayload = (value) => {
    if (value === null || typeof value !== "object") {
        const encoded = JSON.stringify(value);
        return encoded === undefined ? "null" : encoded;
    }
    if (Array.isArray(value)) {
        return `[${value.map((entry) => (0, exports.canonicalizeAodPayload)(entry)).join(",")}]`;
    }
    const entries = Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
    return `{${entries
        .map(([key, entry]) => `${JSON.stringify(key)}:${(0, exports.canonicalizeAodPayload)(entry)}`)
        .join(",")}}`;
};
exports.canonicalizeAodPayload = canonicalizeAodPayload;
const buildAodSigningMessage = (params) => [
    exports.AOD_SIGNATURE_VERSION,
    String(params.timestamp),
    params.nonce,
    params.stripeReaderId,
    params.keyId,
    params.paymentIntentId,
    params.action,
    params.payloadSha256,
].join("\n");
exports.buildAodSigningMessage = buildAodSigningMessage;
exports.refundReturnStatusSchema = zod_1.z.enum([
    "RESTOCKABLE",
    "ACTION_REQUIRED",
    "NON_RESTOCKABLE",
    "PENDING_INSPECTION",
]);
exports.refundReasonSchema = zod_1.z.enum(["duplicate", "fraudulent", "requested_by_customer"]);
exports.aodSignatureHeadersSchema = zod_1.z
    .object({
    "x-aod-key-id": zod_1.z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
    "x-aod-timestamp": zod_1.z.coerce.number().int().nonnegative(),
    "x-aod-nonce": zod_1.z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
    "x-aod-signature": zod_1.z
        .string()
        .regex(/^[A-Za-z0-9+/]+={0,2}$/)
        .max(256),
})
    .strict();
const aodReturnDispositionSchema = zod_1.z
    .object({
    returnToStock: zod_1.z.boolean(),
})
    .strict();
const webReturnDispositionSchema = zod_1.z
    .object({
    returnStatus: exports.refundReturnStatusSchema,
})
    .strict();
exports.refundItemSelectionSchema = zod_1.z
    .object({
    quantity: zod_1.z.number().int().positive(),
    disposition: zod_1.z.union([aodReturnDispositionSchema, webReturnDispositionSchema]),
})
    .strict();
exports.itemizedRefundPreviewRequestSchema = zod_1.z
    .object({
    mode: zod_1.z.literal("items"),
    items: zod_1.z.record(zod_1.z.string().min(1), exports.refundItemSelectionSchema),
    includeTip: zod_1.z.boolean().default(false),
    restockingFeeAmount: zod_1.z.number().int().nonnegative().default(0),
    stripeReaderId: zod_1.z
        .string()
        .regex(/^tmr_[A-Za-z0-9]+$/)
        .optional(),
})
    .strict();
exports.customRefundPreviewRequestSchema = zod_1.z
    .object({
    mode: zod_1.z.literal("custom"),
    amount: zod_1.z.number().int().positive(),
    items: zod_1.z.record(zod_1.z.string().min(1), exports.refundItemSelectionSchema).optional(),
    stripeReaderId: zod_1.z
        .string()
        .regex(/^tmr_[A-Za-z0-9]+$/)
        .optional(),
})
    .strict();
exports.refundPreviewRequestSchema = zod_1.z.discriminatedUnion("mode", [
    exports.itemizedRefundPreviewRequestSchema,
    exports.customRefundPreviewRequestSchema,
]);
exports.refundPaymentIntentParamsSchema = zod_1.z
    .object({ paymentIntentId: zod_1.z.string().min(1) })
    .strict();
exports.refundPreviewIssueCodeSchema = zod_1.z.enum([
    "NO_ITEMS_SELECTED",
    "CART_NOT_AVAILABLE",
    "INVALID_CART_ITEM",
    "INVALID_RETURN_DISPOSITION",
    "QUANTITY_EXCEEDS_AVAILABLE",
    "TAX_DATA_UNAVAILABLE",
    "ITEM_VALUE_UNAVAILABLE",
    "TIP_ATTRIBUTION_UNKNOWN",
    "RESTOCKING_FEE_EXCEEDS_REFUND",
    "AMOUNT_EXCEEDS_REMAINING",
    "PAYMENT_NOT_REFUNDABLE",
]);
exports.refundPreviewIssueSchema = zod_1.z
    .object({
    code: exports.refundPreviewIssueCodeSchema,
    message: zod_1.z.string(),
    cartKey: zod_1.z.string().optional(),
})
    .strict();
exports.refundQuoteLineSchema = zod_1.z
    .object({
    cartKey: zod_1.z.string(),
    priceId: zod_1.z.string(),
    productId: zod_1.z.string(),
    sku: zod_1.z.string().optional(),
    locationId: zod_1.z.string().optional(),
    productName: zod_1.z.string().optional(),
    priceName: zod_1.z.string().optional(),
    purchasedQuantity: zod_1.z.number().int().nonnegative(),
    previouslyReturnedQuantity: zod_1.z.number().int().nonnegative(),
    reservedQuantity: zod_1.z.number().int().nonnegative(),
    availableQuantity: zod_1.z.number().int().nonnegative(),
    selectedQuantity: zod_1.z.number().int().nonnegative(),
    usedInventory: zod_1.z.boolean(),
    returnStatus: exports.refundReturnStatusSchema.optional(),
    taxBehavior: zod_1.z.enum(["inclusive", "exclusive", "none"]),
    unitRefundAmount: zod_1.z.number().int().nonnegative(),
    lineSubtotalAmount: zod_1.z.number().int().nonnegative(),
    lineTaxAmount: zod_1.z.number().int().nonnegative(),
    lineRefundAmount: zod_1.z.number().int().nonnegative(),
})
    .strict();
exports.refundQuoteSummarySchema = zod_1.z
    .object({
    subtotalAmount: zod_1.z.number().int().nonnegative(),
    taxAmount: zod_1.z.number().int().nonnegative(),
    tipAmount: zod_1.z.number().int().nonnegative(),
    restockingFeeAmount: zod_1.z.number().int().nonnegative(),
    totalRefundAmount: zod_1.z.number().int().nonnegative(),
    paymentAmount: zod_1.z.number().int().nonnegative(),
    alreadyRefundedAmount: zod_1.z.number().int().nonnegative(),
    reservedRefundAmount: zod_1.z.number().int().nonnegative(),
    remainingRefundableAmount: zod_1.z.number().int().nonnegative(),
    remainingAfterRefundAmount: zod_1.z.number().int().nonnegative(),
    originalTipAmount: zod_1.z.number().int().nonnegative(),
    remainingTipAmount: zod_1.z.number().int().nonnegative(),
    tipAttributionKnown: zod_1.z.boolean(),
})
    .strict();
exports.refundPreviewResponseSchema = zod_1.z
    .object({
    paymentIntentId: zod_1.z.string(),
    currency: zod_1.z.string(),
    mode: zod_1.z.enum(["items", "custom"]),
    quoteToken: zod_1.z.string().nullable(),
    expiresAt: zod_1.z.string(),
    canExecute: zod_1.z.boolean(),
    issues: zod_1.z.array(exports.refundPreviewIssueSchema),
    lines: zod_1.z.array(exports.refundQuoteLineSchema),
    summary: exports.refundQuoteSummarySchema,
})
    .strict();
exports.executeRefundRequestSchema = zod_1.z
    .object({
    quoteToken: zod_1.z.string().min(1),
    reason: exports.refundReasonSchema,
    reasonDetails: zod_1.z.string().trim().max(500).optional(),
    receiptEmail: base_js_1.emailSchema.optional(),
    stripeReaderId: zod_1.z
        .string()
        .regex(/^tmr_[A-Za-z0-9]+$/)
        .optional(),
})
    .strict();
exports.executeRefundResponseSchema = zod_1.z
    .object({
    operationId: zod_1.z.string(),
    paymentIntentId: zod_1.z.string(),
    refundId: zod_1.z.string().optional(),
    status: zod_1.z.enum(["succeeded", "pending", "requires_action", "failed", "canceled"]),
    inventoryFinalized: zod_1.z.boolean(),
    readerId: zod_1.z.string().optional(),
    stripeReaderId: zod_1.z.string().optional(),
    amount: zod_1.z.number().int().nonnegative(),
    currency: zod_1.z.string(),
    summary: exports.refundQuoteSummarySchema,
})
    .strict();
//# sourceMappingURL=refund-quote.schemas.js.map